import React from 'react';
import { ui } from '../../config/uiSizes';
import { parseJobProgress } from '../../utils/jobProgress';

interface JobProgressBarProps {
    /** The `job3Import.notes` string persisted by the backend progress tracker. */
    notes: string | null | undefined;
}

/**
 * Renders a percentage progress bar for a long-running import, parsed from the
 * backend's `job3Import.notes`. Renders nothing when the note is not a progress
 * line (so it only appears while an import is actively reporting progress).
 */
const JobProgressBar: React.FC<JobProgressBarProps> = ({ notes }) => {
    const progress = parseJobProgress(notes);
    if (!progress) return null;

    const pctLabel = `${Math.round(progress.percent)}%`;
    const countLabel =
        typeof progress.processed === 'number' && typeof progress.total === 'number' && progress.total > 0
            ? `${progress.processed} of ${progress.total} resources`
            : null;

    return (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className={`flex items-center justify-between ${ui.mb.sm}`}>
                <span
                    className={`${ui.text.label} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide`}
                >
                    Import Progress
                </span>
                <span className={`${ui.text.label} font-bold text-blue-700 dark:text-blue-300`}>
                    {pctLabel}
                </span>
            </div>
            <div
                className={`w-full ${ui.widthAndHeight.progressBar} bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden`}
                role="progressbar"
                aria-valuenow={Math.round(progress.percent)}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <div
                    className={`${ui.widthAndHeight.progressBar} bg-blue-600 dark:bg-blue-500 rounded-full transition-all duration-500`}
                    style={{ width: `${progress.percent}%` }}
                />
            </div>
            <div className={`flex items-center justify-between ${ui.text.xs} text-gray-500 dark:text-gray-400 mt-2`}>
                <span className="truncate">
                    {progress.statusMessage ?? (progress.stage ? `Stage: ${progress.stage}` : '')}
                </span>
                {countLabel && <span className="flex-shrink-0 ml-2">{countLabel}</span>}
            </div>
        </div>
    );
};

export default JobProgressBar;
